import { useAuth } from "@/hooks/use-auth";
import { useLanguageSync } from "@/hooks/use-language-sync";
import { MDXProvider } from "@mdx-js/react";
import { AppShell } from "@msuite/ui/app-shell";
import { AuthContext } from "@msuite/ui/auth";
import { FloatingControlProvider } from "@msuite/ui/floating-control";
import { NuqsAdapter } from "@msuite/ui/libs/nuqs";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { guardString } from "@msuite/utils/strings";
import { ZeroProvider } from "@msuite/zero/react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { AuthLoading, SignedIn, SignedOut } from "./components/auth";
import { RememberConnectCode } from "./components/partners/remember-connect-code";
import { RecordingPanel } from "./components/wiki/recording-panel";
import { RecordingRecovery } from "./components/wiki/recording-recovery";
import { Chat } from "./components/chat";
import { NavigationWrapper } from "./components/navigation-wrapper";
import "./globals.css";
import { useSentryUser } from "./sentry";
import { GeneralDropzone } from "./components/general-dropzone";
import { FILE_INBOX_PATH } from "./sites/data/file-inbox/path";
import { TicketFileDrop } from "./components/ticket-file-drop";

/** Lazy Components */
const Absences = lazy(() => import("./sites/absences"));
const Attendances = lazy(() => import("./sites/attendances"));
const CreateOrganization = lazy(() => import("./sites/organization/create-organization"));
const MySpace = lazy(() => import("./sites/my-space"));
const Wiki = lazy(() => import("./sites/wiki"));
const Project = lazy(() => import("./sites/projects/project"));
const Projects = lazy(() => import("./sites/projects/projects"));
const ProjectsMap = lazy(() => import("./sites/projects/projects-map"));
const Tasks = lazy(() => import("./sites/tasks"));
const SignUp = lazy(() => import("./sites/sign-up"));
const Plan = lazy(() => import("./sites/plan"));
const SignIn = lazy(() => import("./sites/sign-in"));
const Dashboard = lazy(() => import("./sites/dashboard"));
const Contacts = lazy(() => import("./sites/contacts/contacts"));
const Calculations = lazy(() => import("./sites/calculations/calculations"));
const Calculation = lazy(() => import("./sites/calculations/calculation"));
const ContactMembers = lazy(() => import("./sites/contacts/contact-members"));
const Contact = lazy(() => import("./sites/contacts/contact"));
const SignUpInvite = lazy(() => import("./sites/sign-up/invite"));
const ForgotPassword = lazy(() => import("./sites/forgot-password"));
const ResetPassword = lazy(() => import("./sites/reset-password"));
const Invoices = lazy(() => import("./sites/controlling/invoices"));
const IncomingInvoices = lazy(() => import("./sites/controlling/incoming-invoices"));
const IncomingInvoice = lazy(() => import("./sites/controlling/incoming-invoice"));
const IncomingInvoiceReminders = lazy(
	() => import("./sites/controlling/incoming-invoice-reminders"),
);
const IncomingInvoiceDeliveryNotes = lazy(
	() => import("./sites/controlling/incoming-invoice-delivery-notes"),
);
const IncomingInvoicePaymentRuns = lazy(
	() => import("./sites/controlling/incoming-invoice-payment-runs"),
);
const IncomingInvoiceApprovals = lazy(
	() => import("./sites/controlling/incoming-invoice-approvals"),
);
const ControllingDashboard = lazy(() => import("./sites/controlling/controlling-dashboard"));
const Partners = lazy(() => import("./sites/partners/partners"));
const Partner = lazy(() => import("./sites/partners/partner"));
const ConnectPartner = lazy(() => import("./sites/connect"));
const Users = lazy(() => import("./sites/users/users"));
const User = lazy(() => import("./sites/users/user"));
const UserAbsences = lazy(() => import("./sites/users/user-absences"));
const UserAttendances = lazy(() => import("./sites/users/user-attendances"));
const OrganizationSettings = lazy(() => import("./sites/organization/settings"));
const Mailbox = lazy(() => import("./sites/organization/mailbox"));
const CreateProtocol = lazy(() => import("./sites/protocols/create-protocol"));
const EditProtocol = lazy(() => import("./sites/protocols/edit-protocol"));
const Protocols = lazy(() => import("./sites/protocols/protocols"));
const Protocol = lazy(() => import("./sites/protocols/protocol"));
const Vehicles = lazy(() => import("./sites/vehicles/vehicles"));
const Vehicle = lazy(() => import("./sites/vehicles/vehicle"));
const FileInbox = lazy(() => import("./sites/data/file-inbox"));
const Services = lazy(() => import("./sites/data/services"));
const Service = lazy(() => import("./sites/data/service"));
const Command = lazy(() => import("./sites/command"));

const Articles = lazy(() => import("./sites/data/articles"));
const Article = lazy(() => import("./sites/data/article"));
const ArticleGroups = lazy(() => import("./sites/data/article-groups"));
const ArticleProductTypes = lazy(() => import("./sites/data/article-product-types"));
const OrderTemplates = lazy(() => import("./sites/data/order-templates"));
const TourWaves = lazy(() => import("./sites/data/tour-waves"));
const Orders = lazy(() => import("./sites/orders/orders"));
const Order = lazy(() => import("./sites/orders/order"));
const Suborder = lazy(() => import("./sites/orders/suborder"));
const DataOrders = lazy(() => import("./sites/data/orders"));
const LoadCarriers = lazy(() => import("./sites/data/load-carriers"));
const LoadCarrier = lazy(() => import("./sites/data/load-carrier"));
const TransferCarriers = lazy(() => import("./sites/data/transfer-carriers"));
const TransferCarrier = lazy(() => import("./sites/data/transfer-carrier"));
const WarehouseZones = lazy(() => import("./sites/data/warehouse-zones"));
const WarehouseZone = lazy(() => import("./sites/data/warehouse-zone"));
const TransportOrders = lazy(() => import("./sites/data/transport-orders"));
const DataReturnOrders = lazy(() => import("./sites/data/return-orders"));
const DataReturnOrder = lazy(() => import("./sites/data/return-order"));

const Substitutions = lazy(() => import("./sites/substitutions/substitutions"));
const Substitution = lazy(() => import("./sites/substitutions/substitution"));
const CalculationEditor = lazy(() => import("./sites/calculations/calculation-editor"));
const CalculationInitialization = lazy(
	() => import("./sites/calculations/calculation-initialization"),
);
const Measurements = lazy(() => import("./sites/measurements/measurements"));
const MeasurementManagers = lazy(() => import("./sites/controlling/measurement-managers"));
const ControllingMeasurement = lazy(() => import("./sites/controlling/controlling-measurement"));

/**
 * The local "sign in as…" picker — the single gate and the single mount point for it.
 *
 * `process.env.NODE_ENV` is a build-time literal in both toolchains (`vite.config.ts` defines it
 * from `mode`, `packages/forge/src/build.ts` pins it to "production"), so in a production build this
 * folds to `null` and the `import()` disappears with the dead branch, taking the picker, its
 * grouping helper and its hook out of the bundle entirely. The `bun run build` step greps `dist/`
 * to keep that honest rather than assumed.
 *
 * Rendered fixed-position, so one mount serves both the signed-out sign-in page and the running
 * app. That is why `sites/sign-in/sign-in.tsx` needs no change and its Cypress selectors are safe.
 */
const DevUserPicker =
	process.env.NODE_ENV === "production" ? null : lazy(() => import("./components/dev"));

export function App() {
	/** Hooks */
	const { organization, user, session, citadel } = useAuth();

	/** Sentry */
	useSentryUser();

	/** Language */
	useLanguageSync();

	/** Guard */
	if (session.isPending) return <LoadingScreen />;

	/** Render */
	return (
		<AuthContext.Provider value={{ user }}>
			<ZeroProvider
				userId={guardString(user?.id)}
				orgId={organization?.id ?? ""}
				// The fixed ticket rule's answer for the optimistic mutator runs — undefined while
				// the rules are resolving, in which case the server's run decides alone.
				arrangeableTicketProjects={citadel?.arrangeableTicketProjects()}
			>
				<MDXProvider>
					<NuqsAdapter>
						<FloatingControlProvider>
							<AppShell
								topbarNavigation={() => <NavigationWrapper variant="topbar" />}
								sidebarNavigation={() => <NavigationWrapper variant="sidebar" />}
							>
								<SignedIn>
									<Chat />
									<RecordingPanel />
									<RecordingRecovery />
									<GeneralDropzone />
									<TicketFileDrop />
								</SignedIn>
								{DevUserPicker && (
									<Suspense fallback={null}>
										<DevUserPicker />
									</Suspense>
								)}
								<Suspense fallback={<LoadingScreen />}>
									<Routes>
										<Route
											path="/sign-up/invite"
											element={<SignUpInvite />}
										/>
										<Route
											path="/connect/:code"
											element={<RememberConnectCode />}
										/>
									</Routes>
									<SignedOut>
										<Routes>
											<Route
												path="*"
												element={<SignIn />}
											/>
											<Route
												path="/sign-up"
												element={<SignUp />}
											/>
											<Route
												path="/forgot-password"
												element={<ForgotPassword />}
											/>
											<Route
												path="/reset-password"
												element={<ResetPassword />}
											/>
										</Routes>
									</SignedOut>
									<AuthLoading>
										<Routes>
											<Route
												path="*"
												element={<LoadingScreen />}
											/>
										</Routes>
									</AuthLoading>
									<SignedIn>
										{organization.orgId ? (
											<Routes>
												<Route
													path="/"
													element={<Navigate to="/dashboard" />}
												/>
												<Route
													path="/dashboard"
													element={<Dashboard />}
												/>
												<Route
													path="/command"
													element={<Command />}
												/>
												<Route path="/projects">
													<Route
														path="/projects"
														element={<Projects />}
													/>
													<Route
														path="/projects/map"
														element={<ProjectsMap />}
													/>
													<Route
														path="/projects/:projectId"
														element={<Project />}
													/>
												</Route>
												<Route path="/users">
													<Route
														path="/users"
														element={<Users />}
													/>
													<Route
														path="/users/absences/all"
														element={<UserAbsences />}
													/>
													<Route
														path="/users/attendances/all"
														element={<UserAttendances />}
													/>
													<Route
														path="/users/:userId"
														element={<User />}
													/>
												</Route>
												<Route
													path="/plan"
													element={<Plan />}
												/>
												<Route path="/partners">
													<Route
														path="/partners"
														element={<Partners />}
													/>
													<Route
														path="/partners/:partnerId"
														element={<Partner />}
													/>
												</Route>
												<Route
													path="/connect/:code"
													element={<ConnectPartner />}
												/>
												<Route path="/contacts">
													<Route
														path="/contacts"
														element={<Contacts />}
													/>
													<Route
														path="/contacts/members"
														element={<ContactMembers />}
													/>
													<Route
														path="/contacts/:contactId"
														element={<Contact />}
													/>
												</Route>
												<Route path="/calculations">
													<Route
														path="/calculations"
														element={<Calculations />}
													/>
													<Route
														path="/calculations/:calculationId"
														element={<Calculation />}
													/>
													<Route
														path="/calculations/:calculationId/editor"
														element={<CalculationEditor />}
													/>
													<Route
														path="/calculations/init"
														element={<CalculationInitialization />}
													/>
												</Route>
												<Route
													path="/attendances"
													element={<Attendances />}
												/>
												<Route path="/protocols">
													<Route
														path="/protocols"
														element={<Protocols />}
													/>
													<Route
														path="/protocols/:protocolId"
														element={<Protocol />}
													/>
													<Route
														path="/protocols/create"
														element={<CreateProtocol />}
													/>
													<Route
														path="/protocols/:protocolId/edit"
														element={<EditProtocol />}
													/>
												</Route>
												<Route path="/controlling">
													<Route
														path="/controlling"
														element={<Invoices />}
													/>
													<Route
														path="/controlling/invoices"
														element={<Invoices />}
													/>
													<Route
														path="/controlling/incoming-invoices"
														element={<IncomingInvoices />}
													/>
													<Route
														path="/controlling/dashboard"
														element={<ControllingDashboard />}
													/>
													<Route
														path="/controlling/incoming-invoices/reminders"
														element={<IncomingInvoiceReminders />}
													/>
													<Route
														path="/controlling/incoming-invoices/delivery-notes"
														element={<IncomingInvoiceDeliveryNotes />}
													/>
													<Route
														path="/controlling/incoming-invoices/payment-runs"
														element={<IncomingInvoicePaymentRuns />}
													/>
													<Route
														path="/controlling/incoming-invoices/approvals"
														element={<IncomingInvoiceApprovals />}
													/>
													<Route
														path="/controlling/incoming-invoices/:incomingInvoiceId"
														element={<IncomingInvoice />}
													/>
													<Route
														path="/controlling/measurements"
														element={<MeasurementManagers />}
													/>
													<Route
														path="/controlling/measurements/:userId"
														element={<Measurements type="controlling" />}
													/>
													<Route
														path="/controlling/measurements/:userId/:projectId"
														element={<ControllingMeasurement />}
													/>
												</Route>
												<Route
													path="/tasks"
													element={<Tasks />}
												/>
												<Route
													path="/my-space"
													element={<MySpace />}
												/>
												<Route path="/substitutions">
													<Route
														path="/substitutions"
														element={<Substitutions />}
													/>
													<Route
														path="/substitutions/:substitutionId"
														element={<Substitution />}
													/>
												</Route>
												<Route
													path="/absences"
													element={<Absences />}
												/>
												<Route path="/orders">
													<Route
														path="/orders"
														element={<Orders />}
													/>
													<Route
														path="/orders/:orderId"
														element={<Order />}
													/>
													<Route
														path="/orders/:orderId/suborders/:suborderId"
														element={<Suborder />}
													/>
												</Route>
												<Route path="/organization">
													<Route
														path="/organization/create"
														element={<CreateOrganization />}
													/>
													<Route
														path="/organization/settings"
														element={<OrganizationSettings />}
													/>
													<Route
														path="/organization/settings/mailboxes/:mailboxId"
														element={<Mailbox />}
													/>
													<Route path="/organization/data">
														<Route
															path={FILE_INBOX_PATH}
															element={<FileInbox />}
														/>
														<Route
															path="/organization/data/services"
															element={<Services />}
														/>
														<Route
															path="/organization/data/services/:serviceId"
															element={<Service />}
														/>
														<Route path="/organization/data/stock">
															<Route
																path="/organization/data/stock/articles"
																element={<Articles />}
															/>
															<Route
																path="/organization/data/stock/articles/:articleId"
																element={<Article />}
															/>
															<Route
																path="/organization/data/stock/article-groups"
																element={<ArticleGroups />}
															/>
															<Route
																path="/organization/data/stock/article-product-types"
																element={<ArticleProductTypes />}
															/>
															<Route
																path="/organization/data/stock/orders"
																element={<DataOrders />}
															/>
															<Route
																path="/organization/data/stock/order-templates"
																element={<OrderTemplates />}
															/>
															<Route
																path="/organization/data/stock/tour-waves"
																element={<TourWaves />}
															/>
															<Route
																path="/organization/data/stock/load-carriers"
																element={<LoadCarriers />}
															/>
															<Route
																path="/organization/data/stock/load-carrier/:loadCarrierId"
																element={<LoadCarrier />}
															/>
															<Route
																path="/organization/data/stock/transfer-carriers"
																element={<TransferCarriers />}
															/>
															<Route
																path="/organization/data/stock/transfer-carrier/:transferCarrierId"
																element={<TransferCarrier />}
															/>
															<Route
																path="/organization/data/stock/warehouse-zones"
																element={<WarehouseZones />}
															/>
															<Route
																path="/organization/data/stock/warehouse-zone/:warehouseZoneId"
																element={<WarehouseZone />}
															/>
															<Route
																path="/organization/data/stock/transport-orders"
																element={<TransportOrders />}
															/>
															<Route
																path="/organization/data/stock/return-orders"
																element={<DataReturnOrders />}
															/>
															<Route
																path="/organization/data/stock/return-orders/:returnOrderId"
																element={<DataReturnOrder />}
															/>
														</Route>
													</Route>
												</Route>
												<Route path="/vehicles">
													<Route
														path="/vehicles"
														element={<Vehicles />}
													/>
													<Route
														path="/vehicles/:vehicleId"
														element={<Vehicle />}
													/>
												</Route>
												<Route path="/measurements">
													<Route
														path="/measurements"
														element={<Measurements type="manager" />}
													/>
												</Route>
												<Route path="/wiki">
													<Route
														path="/wiki"
														element={<Wiki />}
													/>
													<Route
														path="/wiki/:spaceId"
														element={<Wiki />}
													/>
													<Route
														path="/wiki/:spaceId/:pageId"
														element={<Wiki />}
													/>
												</Route>
											</Routes>
										) : (
											<Routes>
												<Route
													path="*"
													element={<CreateOrganization />}
												/>
											</Routes>
										)}
									</SignedIn>
								</Suspense>
							</AppShell>
						</FloatingControlProvider>
					</NuqsAdapter>
				</MDXProvider>
			</ZeroProvider>
		</AuthContext.Provider>
	);
}
